/**
 * As contas e as palavras da tela de compras. Fora do componente, porque é o
 * que dá para provar sem montar React.
 */
import { formatarAteDuasCasas, formatarInteiro } from '@/lib/formato';

import type { ItemDaNecessidade, OrigemDaRequisicao, Pedido, SituacaoDoPedido } from './api';

/**
 * A origem escrita. Ela é obrigatória na requisição porque "por que compramos
 * isso" não tem resposta três meses depois sem ela, e o v6 nomeia as quatro.
 */
export function origemEscrita(origem: OrigemDaRequisicao): string {
  if (origem === 'revisao') return 'Revisão preventiva';
  if (origem === 'ordem_servico') return 'Ordem de serviço';
  if (origem === 'minimo') return 'Mínimo de estoque';
  if (origem === 'consumo') return 'Previsão de consumo';
  return 'Registrada à mão';
}

/**
 * A situação do pedido, escrita — e não em selo, pela mesma razão da medição:
 * os seis status do manual descrevem uma máquina, e um pedido não está
 * "Operando". Inventar um sétimo conjunto de cores é decisão de quem assina o
 * manual, não da tela.
 */
export function situacaoEscrita(situacao: SituacaoDoPedido): string {
  if (situacao === 'rascunho') return 'Rascunho';
  if (situacao === 'aguardando aprovacao') return 'Aguardando aprovação';
  if (situacao === 'aprovado') return 'Aprovado';
  if (situacao === 'emitido') return 'Emitido';
  if (situacao === 'recebido') return 'Recebido';
  return 'Cancelado';
}

export interface TotaisDaCompra {
  /** Quantos itens ainda precisam de pedido. É o número da tela. */
  readonly aComprar: number;
  /** Desses, quantos são item crítico. */
  readonly criticosAComprar: number;
  /** Itens cuja falta já está coberta por pedido aberto. */
  readonly jaPedidos: number;
  /** Quanto está comprometido em pedido aprovado ou emitido, em reais. */
  readonly comprometido: number;
  /** Pedidos emitidos com item faltando chegar. */
  readonly pedidosPendentes: number;
}

/**
 * Os totais da faixa.
 *
 * `aComprar` conta item com `comprar > 0`, e `jaPedidos` conta o item que
 * ainda tem demanda descoberta pelo estoque mas já está em pedido. A distinção
 * é o módulo inteiro: sem ela, a mesma linha apareceria como falta toda semana
 * e alguém compraria de novo — o risco que o v6 nomeia.
 */
export function totaisDaCompra(
  necessidade: readonly ItemDaNecessidade[],
  pedidos: readonly Pedido[],
): TotaisDaCompra {
  const comprar = necessidade.filter((i) => i.comprar > 0);
  const abertos = pedidos.filter((p) => p.situacao === 'aprovado' || p.situacao === 'emitido');
  return {
    aComprar: comprar.length,
    criticosAComprar: comprar.filter((i) => i.critico).length,
    jaPedidos: necessidade.filter((i) => i.comprar === 0 && i.emPedido > 0).length,
    comprometido: abertos.reduce((s, p) => s + p.valorEstimado, 0),
    pedidosPendentes: pedidos.filter((p) => p.situacao === 'emitido' && p.faltaChegar > 0).length,
  };
}

export type EstadoDaFaixa = 'carregando' | 'falhou' | 'pronta';

export function estadoDaFaixa(consulta: { readonly data: unknown; readonly isError: boolean }): EstadoDaFaixa {
  if (consulta.isError) return 'falhou';
  return consulta.data === undefined ? 'carregando' : 'pronta';
}

/**
 * De onde a demanda daquela linha veio, para a tela não mostrar um número sem
 * procedência. Um item pode estar nas duas listas, e aí as duas aparecem.
 */
export function deOndeVeioADemanda(item: ItemDaNecessidade): string {
  const partes: string[] = [];
  if (item.daPreventiva) partes.push('preventiva vencendo');
  if (item.doMinimo) partes.push('abaixo do mínimo');
  if (item.doConsumo) partes.push('previsão de consumo');
  if (item.daRequisicao) partes.push('necessidade registrada');
  if (partes.length === 0) return 'demanda sem origem registrada';
  if (partes.length === 1) return partes[0] ?? '';
  return `${partes.slice(0, -1).join(', ')} e ${partes.at(-1) ?? ''}`;
}

/**
 * A conta da previsão de consumo daquela linha (0091), escrita: "0,13 un/dia ×
 * 10 dias = 1,33 un". Nulo quando o item não tem previsão. A premissa geral
 * (janela, de onde vem o prazo, a regra da preventiva) vem do servidor.
 */
export function previsaoEscrita(item: ItemDaNecessidade): string | null {
  if (!item.doConsumo || item.consumoDiario == null || item.prazoDaPrevisao == null || item.demandaDoConsumo == null) return null;
  const dias = item.prazoDaPrevisao === 1 ? '1 dia' : `${formatarInteiro(item.prazoDaPrevisao)} dias`;
  return `${formatarAteDuasCasas(item.consumoDiario)} ${item.unidade}/dia × ${dias} = ${formatarAteDuasCasas(item.demandaDoConsumo)} ${item.unidade}`;
}

/**
 * A segunda linha da Demanda (R06, p.71): "3,2 un/mês × 2,5 meses". O mês é
 * de 30 dias, a mesma conta da previsão (consumo diário × prazo). Prazo menor
 * que um mês fica em dias: "0,4 un/dia × 12 dias".
 */
export function previsaoNaDemanda(item: ItemDaNecessidade): string | null {
  if (!item.doConsumo || item.consumoDiario == null || item.prazoDaPrevisao == null) return null;
  if (item.prazoDaPrevisao < 30) {
    const dias = item.prazoDaPrevisao === 1 ? '1 dia' : `${formatarInteiro(item.prazoDaPrevisao)} dias`;
    return `${formatarAteDuasCasas(Math.round(item.consumoDiario * 10) / 10)} ${item.unidade}/dia × ${dias}`;
  }
  const porMes = Math.round(item.consumoDiario * 30 * 10) / 10;
  const meses = Math.round((item.prazoDaPrevisao / 30) * 10) / 10;
  return `${formatarAteDuasCasas(porMes)} ${item.unidade}/mês × ${formatarAteDuasCasas(meses)} ${meses === 1 ? 'mês' : 'meses'}`;
}

/**
 * O que dá para fazer com este pedido agora, e o motivo quando não dá.
 *
 * O motivo viaja junto: ação desligada sem explicação manda a pessoa procurar
 * a permissão que falta num lugar que a tela não diz.
 */
export function porqueNaoDa(
  pedido: Pedido,
  acao: 'aprovar' | 'emitir' | 'cancelar' | 'receber',
  permissoes: readonly string[],
): string | undefined {
  const licenca: Record<typeof acao, string> = {
    aprovar: 'pedido.aprovar',
    cancelar: 'pedido.aprovar',
    emitir: 'pedido.gravar',
    receber: 'recebimento.gravar',
  };
  if (!permissoes.includes(licenca[acao])) {
    return `Você não tem a licença ${licenca[acao]}. Peça a liberação em Cadastros · Governança.`;
  }
  if (pedido.situacao === 'cancelado') return 'Pedido cancelado não volta atrás.';

  if (acao === 'aprovar') {
    if (pedido.situacao !== 'rascunho' && pedido.situacao !== 'aguardando aprovacao') {
      return 'Este pedido já foi aprovado.';
    }
    if (pedido.itens === 0) {
      return 'O pedido não tem nenhum item. Acrescente os itens antes de aprovar — aprovar o vazio compromete um valor que ninguém escreveu.';
    }
    return undefined;
  }
  if (acao === 'emitir') {
    if (pedido.situacao === 'rascunho' || pedido.situacao === 'aguardando aprovacao') {
      return 'O pedido sai para o fornecedor depois de aprovado. Peça a aprovação a quem tem a licença.';
    }
    if (pedido.situacao !== 'aprovado') return 'Este pedido já foi emitido.';
    return undefined;
  }
  if (acao === 'cancelar') {
    if (pedido.situacao === 'recebido') return 'O pedido já chegou por inteiro. Para devolver, use a devolução no estoque.';
    return undefined;
  }
  if (pedido.situacao !== 'emitido') {
    return 'Só o pedido emitido recebe. Aprove e emita antes de registrar o que chegou.';
  }
  if (pedido.faltaChegar <= 0) return 'Todos os itens deste pedido já chegaram.';
  return undefined;
}

/* ------------------------------------------------- o pedido pelo endereço */

export interface BuscaDeCompras {
  /** O pedido que o aviso de atraso (0091) abre: `/compras?pedido=<id>`. */
  readonly pedido?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A busca de `/compras`: só o pedido, e só se for um id — link torto cai na tela sem nada marcado. */
export function validarBuscaDeCompras(entrada: Record<string, unknown>): BuscaDeCompras {
  const pedido = entrada['pedido'];
  return typeof pedido === 'string' && UUID.test(pedido) ? { pedido: pedido.toLowerCase() } : {};
}

/** A página da tabela em que o pedido está (1 em diante), ou `null` se ele não está na lista. */
export function paginaDoPedido(pedidos: readonly { readonly id: string }[], id: string, porPagina: number): number | null {
  const indice = pedidos.findIndex((p) => p.id === id);
  return indice === -1 ? null : Math.floor(indice / porPagina) + 1;
}
